<template>
  <div class="optimized-video-container">
    <!-- Video principal -->
    <video
      v-if="videoPath"
      :src="videoPath"
      :class="combinedClasses"
      :preload="preloadStrategy"
      :autoplay="autoplay"
      :muted="muted"
      :loop="loop"
      :playsinline="playsinline"
      :controls="showControls"
      @loadeddata="handleVideoLoaded"
      @error="handleVideoError"
      :style="[videoOpacityStyle, { 
        width: '100%', 
        height: '100%', 
        objectFit: 'cover', 
        objectPosition: 'center',
        position: 'absolute',
        top: 0,
        left: 0
      }]"
    ></video>
  </div>
</template>

<script>
export default {
  name: 'OptimizedVideo',
  props: {
    videoPath: {
      type: String,
      required: true,
    },
    width: {
      type: [String, Number],
      default: null,
    },
    height: {
      type: [String, Number],
      default: null,
    },
    maxWidth: {
      type: String,
      default: '100%',
    },
    maxHeight: {
      type: String,
      default: '100%',
    },
    baseClass: {
      type: String,
      default: '',
    },
    additionalClasses: {
      type: String,
      default: 'mt-0 px-0 mx-0 rounded-xl',
    },
    blurOnHide: {
      type: Boolean,
      default: false,
    },
    showVideo: {
      type: Boolean,
      default: true,
    },
    preloadStrategy: {
      type: String,
      default: 'auto',
      validator: (value) => ['auto', 'metadata', 'none'].includes(value),
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    playsinline: {
      type: Boolean,
      default: true,
    },
    showControls: {
      type: Boolean,
      default: false,
    },
    aspectRatio: {
      type: [String, Number],
      default: null,
    },
    transitionDuration: {
      type: Number,
      default: 300,
    },
    lowQualityPoster: {
      type: String,
      default: '',
    }
  },
  
  data() {
    return {
      isLoaded: false,
      videoError: false,
      isPlaying: false
    };
  },

  computed: {
    combinedClasses() {
      return [
        this.baseClass,
        this.additionalClasses,
        { 'blur-explicit': !this.showVideo && this.blurOnHide },
      ];
    },
    
    videoOpacityStyle() {
      return {
        transition: `opacity ${this.transitionDuration}ms ease-in-out`,
        opacity: this.isLoaded ? 1 : 0,
      };
    }
  },
  
  methods: {
    handleVideoLoaded(event) {
      this.isLoaded = true;
      this.$emit('video-loaded', event);
      
      if (window.performance && window.performance.mark) {
        window.performance.mark('video-loaded');
      }
    },
    
    handleVideoError(error) {
      this.videoError = true;
      this.$emit('video-error', error);
      console.warn(`Error loading video: ${this.videoPath}`, error);
    },
    
    play() {
      const videoElement = this.$el.querySelector('video');
      if (videoElement) {
        videoElement.play().then(() => {
          this.isPlaying = true;
          this.$emit('play');
        }).catch(error => {
          console.warn('Error al reproducir el video:', error);
          this.$emit('play-error', error);
        });
      }
    },
    
    pause() {
      const videoElement = this.$el.querySelector('video');
      if (videoElement) {
        videoElement.pause();
        this.isPlaying = false;
        this.$emit('pause');
      }
    },
    
    togglePlay() {
      const videoElement = this.$el.querySelector('video');
      if (videoElement) {
        if (videoElement.paused) {
          this.play();
        } else {
          this.pause();
        }
      }
    }
  },
}
</script>

<style scoped>
.optimized-video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.blur-explicit {
  filter: blur(10px);
}
</style> 